import React, { useState } from 'react';

interface Artwork {
  src: string;
  title: string;
}

const artworks: Artwork[] = [
  { src: '/art/IMG_6223.jpg', title: 'Untitled I' },
  { src: '/art/IMG_6224.jpg', title: 'Untitled II' },
  { src: '/art/IMG_6225.jpg', title: 'Untitled III' },
  { src: '/art/IMG_6226.jpg', title: 'Untitled IV' },
  { src: '/art/IMG_6227.jpg', title: 'Untitled V' },
  { src: '/art/IMG_6228.jpg', title: 'Untitled VI' },
  { src: '/art/IMG_6229.jpg', title: 'Untitled VII' },
  { src: '/art/IMG_6230.jpg', title: 'Untitled VIII' },
  { src: '/art/IMG_6231.jpg', title: 'Untitled IX' },
  { src: '/art/IMG_6232.jpg', title: 'Untitled X' },
  { src: '/art/IMG_6233.jpg', title: 'Untitled XI' },
  { src: '/art/IMG_6234.jpg', title: 'Untitled XII' },
  { src: '/art/IMG_6235.jpg', title: 'Untitled XIII' },
  { src: '/art/IMG_6236.jpg', title: 'Untitled XIV' },
  { src: '/art/IMG_6237.jpg', title: 'Untitled XV' },
];

const originImage = '/art/what_made_me_start_painting.jpg';

const Art: React.FC = () => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const selectedArtwork =
    selectedIndex === null ? null : artworks[selectedIndex];

  const move = (direction: 'next' | 'previous') => {
    if (selectedIndex === null) return;

    const offset = direction === 'next' ? 1 : -1;
    const nextIndex =
      (selectedIndex + offset + artworks.length) % artworks.length;
    setSelectedIndex(nextIndex);
  };

  return (
    <main className="page-shell art-page">
      <header className="page-title">
        <p className="eyebrow">Art</p>
        <h1>Painting as another way of thinking</h1>
        <p>
          Watercolours, sketches, and experiments about consciousness,
          perception, and the strange usefulness of making something without
          optimizing it.
        </p>
      </header>

      <section className="art-intro">
        <div>
          <p>
            I paint because it is a different kind of thinking. The canvas gives
            logic somewhere to rest, while color, water, and accident do work
            that language cannot.
          </p>
          <p>
            Most of the work is abstract. I am drawn to the tension between form
            and formlessness, and to the way a visual field can hold a state of
            mind before words arrive.
          </p>
        </div>
        <figure>
          <img src={originImage} alt="The painting that inspired Birger to start painting" />
          <figcaption>The painting that made me start painting.</figcaption>
        </figure>
      </section>

      <section className="art-gallery" aria-label="Selected artworks">
        {artworks.map((artwork, index) => (
          <button
            key={artwork.src}
            type="button"
            onClick={() => setSelectedIndex(index)}
            aria-label={`Open ${artwork.title}`}
          >
            <img src={artwork.src} alt={artwork.title} loading="lazy" />
          </button>
        ))}
      </section>

      <section className="art-note">
        <p>
          Teaching artists at Konstfack to work with generative AI changed how I
          see my own practice. Their questions about authorship, meaning, hand,
          tool, image, and intention made painting feel less like a side project
          and more like part of the same inquiry.
        </p>
      </section>

      {selectedArtwork && (
        <div
          className="lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={selectedArtwork.title}
          onClick={() => setSelectedIndex(null)}
        >
          <button
            type="button"
            className="lightbox-close"
            onClick={() => setSelectedIndex(null)}
          >
            Close
          </button>
          <button
            type="button"
            className="lightbox-nav lightbox-prev"
            onClick={(event) => {
              event.stopPropagation();
              move('previous');
            }}
            aria-label="Previous artwork"
          >
            Previous
          </button>
          <img
            src={selectedArtwork.src}
            alt={selectedArtwork.title}
            onClick={(event) => event.stopPropagation()}
          />
          <button
            type="button"
            className="lightbox-nav lightbox-next"
            onClick={(event) => {
              event.stopPropagation();
              move('next');
            }}
            aria-label="Next artwork"
          >
            Next
          </button>
          <p>
            {(selectedIndex ?? 0) + 1} / {artworks.length}
          </p>
        </div>
      )}
    </main>
  );
};

export default Art;
